import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  CHANNEL_CHECK_UNAVAILABLE_MESSAGE,
  GATE_STILL_MISSING_MESSAGE,
  MANDATORY_CHANNELS_MESSAGE,
  type GateChannel,
} from "@bing/shared";
import { openTelegramLink } from "@telegram-apps/sdk";
import { Button } from "../components/Button";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import type { BootErrorAction, SessionStatus } from "../store/session";

/**
 * The MANDATORY-CHANNEL gate: the user must join the platform's required channels before using the
 * mini app (the SAME `RequiredChannel` config the bot's `/start` gate uses). A MIRROR of a server
 * decision, never itself the gate — every api request is re-checked server-side (403
 * `CHANNELS_REQUIRED`), so hiding this component buys nothing. Built from the existing `.screen`
 * tokens so it reads as part of the app in both themes.
 *
 * NOT shown over a live game — like maintenance, a game already in progress finishes; this appears
 * when the player returns to the lobby. ⚠️ A LIVE GAME IS THE GAME VIEW ON A **READY** SESSION, NOT
 * THE GAME VIEW ALONE: a `t_<game>_<roomId>` table link sets `view: "game"` before `/me` has been
 * asked, and the game view only renders once the session is ready — before that the screen is the
 * splash or the boot error. Yielding to the view alone sent the 403 to the boot error screen, which
 * printed «…در کانال‌های زیر عضو شوید:» over nothing, with no button (staging 2026-10-03; on prod
 * 138 boots from 96 devices in 15 days).
 */
export function shouldShowChannelGate(
  channels: GateChannel[] | null,
  view: string,
  status: SessionStatus,
): boolean {
  if (!channels || channels.length === 0) return false;
  return view !== "game" || status !== "ready";
}

/**
 * The headline — THE shared gate sentence (`@bing/shared`), the same one the bot's gate screen
 * shows: a user bounced by the bot and then by the app must read ONE message, not two.
 */
export const CHANNEL_GATE_MESSAGE = MANDATORY_CHANNELS_MESSAGE;

/**
 * Shown when «عضو شدم» ran and the server still says no. ONE line, and the SAME sentence the
 * bot's popup uses (`@bing/shared`): the channels it would have re-listed are right there
 * underneath it, as buttons.
 */
const STILL_MISSING = GATE_STILL_MISSING_MESSAGE;

/**
 * THE LINE A RE-CHECK WITH NO VERDICT SHOWS (card-a5 item 5) — or `undefined` when there was a
 * verdict. Read off the session the App's re-check (`loadProfile`) leaves behind: a failure whose
 * remedy is asking again (`errorAction === "retry"`: the api's 503 `CHANNEL_CHECK_UNAVAILABLE` —
 * Telegram did not answer in time — or no answer from the api at all) is NOT «not a member yet», so
 * the gate says what happened instead, with «عضو شدم» right under it as the retry. A 403
 * `CHANNELS_REQUIRED` is a verdict (its action is none) and keeps «هنوز عضو…».
 */
export function recheckNotice(s: {
  status: SessionStatus;
  error: string | null;
  errorAction: BootErrorAction;
}): string | undefined {
  if (s.status !== "error" || s.errorAction !== "retry") return undefined;
  return s.error ?? CHANNEL_CHECK_UNAVAILABLE_MESSAGE;
}

/**
 * How long the «✅ عضویتت تأیید شد» beat is held before the app takes over.
 *
 * It ADVANCES ON ITS OWN — nobody should have to tap a button to get into the app after they
 * have already gone and joined the channels («بزن بریم!» is a skip, not a toll). Long enough
 * to be read without hurrying: at 1.9s it was gone before it had been.
 */
export const GATE_APPROVED_HOLD_MS = 5000;

/** The gate's own fade-out — it dissolves INTO the app instead of being cut away. */
export const GATE_FADE_MS = 300;

/** A channel with no way in at all (private + the bot could not mint an invite link). */
const NO_LINK_HINT = "لینک عضویت در دسترس نیست — به پشتیبانی اطلاع بده.";

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const faNum = (n: number): string =>
  String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] as string);

/**
 * The private link for this user's join — the bot-created invite link, so Telegram attributes the
 * join (تبلیغات). Order: the per-campaign `joinUrl`, then the channel's stored `inviteLink`, then
 * the public @handle only as a last resort (private channels with no minted link have no button).
 *
 * The public @handle is LAST because a join through it is attributed to no link, so the
 * (campaign×channel) join goes uncounted (تبلیغات §5). A stored `inviteLink` still attributes the
 * join, so it beats the @handle when the api could not mint a fresh per-campaign `joinUrl` (a mint/DB
 * hiccup fails OPEN, or an old api response carries no `joinUrl`) — the user joins either way.
 */
function channelLink(c: GateChannel): string | null {
  if (c.joinUrl) return c.joinUrl;
  if (c.inviteLink) return c.inviteLink;
  return c.username ? `https://t.me/${c.username.replace(/^@/, "")}` : null;
}

export function ChannelGate({
  channels,
  approved = false,
  onRecheck,
  onEnter,
  onLeave,
}: {
  channels: GateChannel[];
  /** The server has just said YES. The gate is kept mounted for this beat (the App holds it)
   *  so the news can actually be delivered — the store clears the gate the instant the check
   *  passes, and without the hold the screen simply vanished mid-tap into a bare lobby. */
  approved?: boolean;
  /**
   * Re-run the membership check (a fresh `getMe`); resolves once the server answers — with the
   * line to show when the check got NO verdict (see {@link recheckNotice}), else nothing.
   */
  onRecheck: () => Promise<string | void>;
  /** The beat is over — hand the screen to the app. */
  onEnter?: () => void;
  /** A way back to the lobby — set where the gate stands in front of a TABLE the game-server
   *  refused (`useTableChannelGate`), not over the whole app (where there is nowhere to go). */
  onLeave?: () => void;
}): ReactNode {
  const [checking, setChecking] = useState(false);
  /** What the last re-check said, when it did not let them in: «not yet», or why there was no answer. */
  const [notice, setNotice] = useState<string | null>(null);
  /** The gate is dissolving into the app (opacity/scale out) — the last step of the beat. */
  const [leaving, setLeaving] = useState(false);

  // The callback identity changes on every App render; hold the latest so the timers below
  // run exactly once instead of restarting.
  const enterRef = useRef(onEnter);
  enterRef.current = onEnter;

  // APPROVED → say so, sound it, hold it, then dissolve.
  useEffect(() => {
    if (!approved) return undefined;
    playSfx("notify");
    const hold = window.setTimeout(() => setLeaving(true), GATE_APPROVED_HOLD_MS);
    return () => window.clearTimeout(hold);
  }, [approved]);

  useEffect(() => {
    if (!leaving) return undefined;
    const done = window.setTimeout(() => enterRef.current?.(), GATE_FADE_MS);
    return () => window.clearTimeout(done);
  }, [leaving]);

  const enterNow = (): void => setLeaving(true); // «بزن بریم!» — skip the hold

  const openChannel = (c: GateChannel): void => {
    const link = channelLink(c);
    if (!link) return;
    // Prefer Telegram's own in-app open (keeps the user inside Telegram); fall back to a new tab.
    try {
      if (openTelegramLink.isAvailable()) {
        openTelegramLink(link);
        return;
      }
    } catch {
      /* not in Telegram → window.open below */
    }
    window.open(link, "_blank");
  };

  const recheck = async (): Promise<void> => {
    setChecking(true);
    setNotice(null);
    let unanswered: string | void = undefined;
    try {
      unanswered = await onRecheck();
    } catch {
      // A 403 CHANNELS_REQUIRED IS the answer — the client already refreshed the channel list
      // from it. Anything else is a transport blip; either way the notice below is the feedback.
    } finally {
      setChecking(false);
      // The gate now survives a PASS too (the App holds it for the approval beat), so this can
      // no longer mean "not a member": the notice itself is rendered only while `!approved`.
      // NO VERDICT (card-a5 item 5) is not «not a member»: the line says why, and the button
      // under it is the retry.
      setNotice(unanswered || STILL_MISSING);
    }
  };

  return (
    // Scrolls. With 3–4 channels the card is taller than a small phone's viewport, and the old
    // `grid/place-items:center` inside a fixed `--app-h` box CLIPPED it — with the check button
    // (the only way forward) cut off the bottom. `min-height:100%` on the inner column keeps it
    // centred when it fits and lets it grow-and-scroll when it does not.
    <div
      style={{
        height: "var(--app-h)",
        overflowY: "auto",
        overscrollBehavior: "contain",
        background: "var(--room-bg)",
        // …and it DISSOLVES into the app rather than being cut away. The lobby is mounted
        // underneath by the time this finishes, so the two cross-fade.
        opacity: leaving ? 0 : 1,
        transform: leaving ? "scale(1.03)" : "none",
        transition: `opacity ${GATE_FADE_MS}ms ease, transform ${GATE_FADE_MS}ms ease`,
      }}
    >
      <div
        style={{
          minHeight: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
          textAlign: "center",
          paddingInline: 24,
          paddingTop: "calc(24px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
          paddingBottom:
            "calc(24px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
        }}
      >
        {approved ? (
          <ApprovedCard onEnter={enterNow} />
        ) : (
          <>
            <div aria-hidden style={{ fontSize: 44, lineHeight: 1 }}>
              📢
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 800,
                color: "rgb(var(--c-on-surface))",
              }}
            >
              {fa.channelGate.title}
            </h1>
            <p style={{ margin: 0, lineHeight: 1.8, color: "rgb(var(--c-on-surface-variant))" }}>
              {CHANNEL_GATE_MESSAGE}
            </p>

            {notice !== null && !approved && (
              <p
                role="status"
                style={{
                  margin: 0,
                  lineHeight: 1.8,
                  fontSize: 13,
                  color: "rgb(var(--c-error))",
                  maxWidth: 340,
                }}
              >
                {notice}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                width: "100%",
                maxWidth: 340,
              }}
            >
              {channels.map((c, i) => {
                const link = channelLink(c);
                const label = c.username ? `@${c.username.replace(/^@/, "")}` : c.title;
                // Numbered, so a user working through 3–4 channels can keep their place.
                const number = channels.length > 1 ? `${faNum(i + 1)}. ` : "";
                return (
                  <div key={c.chatId} style={{ width: "100%" }}>
                    <button
                      type="button"
                      onClick={() => openChannel(c)}
                      disabled={!link}
                      className="btn"
                      style={{ width: "100%", opacity: link ? 1 : 0.55 }}
                    >
                      {number}
                      {/* ⚠️ `dir="auto"` — the label is its OWN bidi run. Laid out flat inside this RTL
                          button, «۲. @bingitesti» painted as «bingitesti@ .۲»: the `@` is a neutral
                          between the numbering and the Latin handle, so it took the paragraph's RTL and
                          landed on the handle's far side. Isolated, a Latin handle or title reads
                          left-to-right and a Persian one right-to-left, and the number stays at the
                          start of the line either way. */}
                      <span dir="auto">{label}</span>
                    </button>
                    {!link && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          lineHeight: 1.7,
                          color: "rgb(var(--c-on-surface-variant))",
                        }}
                      >
                        {NO_LINK_HINT}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => void recheck()}
              disabled={checking}
              className="btn btn--gold"
              style={{ width: "100%", maxWidth: 340, marginTop: 4 }}
            >
              {checking ? fa.channelGate.checking : fa.channelGate.joined}
            </button>
            {onLeave ? (
              <button
                type="button"
                onClick={onLeave}
                className="btn"
                style={{ width: "100%", maxWidth: 340 }}
              >
                {fa.channelGate.back}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * THE MEMBERSHIP CAME BACK APPROVED.
 *
 * Before this, passing the gate looked like a bug: you tapped «عضو شدم», the screen vanished
 * and you were simply somewhere else. Nothing said you were in. Now the news is delivered —
 * with the same spring-and-glow the game's win card uses — and the gate dissolves into the
 * lobby behind it instead of being cut away.
 */
function ApprovedCard({ onEnter }: { onEnter: () => void }): ReactNode {
  return (
    <div
      className="end-card end-card--win"
      role="status"
      aria-live="polite"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: "var(--radius-lg)",
        padding: "30px 24px",
        width: "100%",
        maxWidth: 340,
        boxShadow: "0 24px 60px rgba(0,0,0,.35)",
      }}
    >
      <div aria-hidden style={{ fontSize: 52, lineHeight: 1 }}>
        🎉
      </div>
      <h1
        style={{
          margin: "12px 0 6px",
          fontSize: 21,
          fontWeight: 800,
          color: "rgb(var(--c-on-surface))",
        }}
      >
        ✅ {fa.channelGate.approvedTitle}
      </h1>
      <p style={{ margin: "0 0 18px", lineHeight: 1.8, color: "rgb(var(--c-on-surface-variant))" }}>
        {fa.channelGate.approvedBody}
      </p>
      <Button variant="primary" onClick={onEnter}>
        {fa.channelGate.approvedCta}
      </Button>
    </div>
  );
}
